import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, Lock, Send, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { listings, offerings, sampleChat } from "@/mocks/residual";
import { formatDateTime, formatNumber, formatUsd } from "@/lib/format";
import type { ChatMessage } from "@/types/residual";
export function MarketplaceListingPage(t0) {
  const $ = _c(122);
  const {
    listingId,
    onBack
  } = t0;
  let t1;
  if ($[0] !== listingId) {
    t1 = listings.find(l => l.id === listingId);
    $[0] = listingId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const listing = t1;
  let t2;
  if ($[2] !== listing?.offeringId) {
    t2 = offerings.find(o => o.id === listing?.offeringId);
    $[2] = listing?.offeringId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const offering = t2;
  let t3;
  if ($[4] !== listing) {
    t3 = () => listing ? sampleChat(listing.id) : [];
    $[4] = listing;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [thread, setThread] = useState(t3);
  const [draft, setDraft] = useState("");
  const [offerTokens, setOfferTokens] = useState(listing?.tokensRemaining ?? 1);
  const [offerPrice, setOfferPrice] = useState(listing?.askingPriceUsd ?? 0);
  if (!listing || !offering) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ArrowLeft className="size-4" />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== onBack) {
      t5 = <Button variant="ghost" size="sm" onClick={onBack}>{t4} Back</Button>;
      $[7] = onBack;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <p className="text-sm text-muted-foreground">Listing not found.</p>;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== t5) {
      t7 = <div className="space-y-4">{t5}{t6}</div>;
      $[10] = t5;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t4;
  if ($[12] !== thread) {
    t4 = [...thread].reverse().find(_temp)?.offer;
    $[12] = thread;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const lastOffer = t4;
  let t5;
  if ($[14] !== draft) {
    t5 = () => {
      if (!draft.trim()) {
        return;
      }
      setThread(prev => [...prev, {
        id: `m_${Date.now()}`,
        authorId: "self",
        authorLabel: "You",
        role: "buyer",
        body: draft.trim(),
        at: new Date().toISOString()
      }]);
      setDraft("");
    };
    $[14] = draft;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const sendMessage = t5;
  let t6;
  if ($[16] !== offerPrice || $[17] !== offerTokens) {
    t6 = () => {
      setThread(prev_0 => [...prev_0, {
        id: `m_${Date.now()}`,
        authorId: "self",
        authorLabel: "You",
        role: "buyer",
        body: `Counter offer: ${offerTokens} × ${formatUsd(offerPrice)}`,
        at: new Date().toISOString(),
        offer: {
          tokens: offerTokens,
          pricePerTokenUsd: offerPrice
        }
      }]);
    };
    $[16] = offerPrice;
    $[17] = offerTokens;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const sendOffer = t6;
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowLeft className="size-4" />;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== onBack) {
    t8 = <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">{t7} Back to marketplace</Button>;
    $[20] = onBack;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `url(${offering.heroUrl})`;
  let t10;
  if ($[22] !== t9) {
    t10 = <div className="size-14 shrink-0 rounded-lg bg-cover bg-center" style={{
      backgroundImage: t9
    }} />;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== offering.title) {
    t11 = <div className="text-sm font-semibold leading-tight">{offering.title}</div>;
    $[24] = offering.title;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== offering.chain || $[27] !== offering.tokenSymbol) {
    t12 = <div className="text-xs text-muted-foreground">{offering.tokenSymbol} on {offering.chain}</div>;
    $[26] = offering.chain;
    $[27] = offering.tokenSymbol;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== listing.askingPriceUsd) {
    t13 = formatUsd(listing.askingPriceUsd);
    $[29] = listing.askingPriceUsd;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13) {
    t14 = <span className="font-semibold tabular-nums">{t13}</span>;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== listing.tokensRemaining) {
    t15 = formatNumber(listing.tokensRemaining);
    $[33] = listing.tokensRemaining;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== listing.tokens) {
    t16 = formatNumber(listing.tokens);
    $[35] = listing.tokens;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <span className="text-muted-foreground">{t15} of{" "}{t16} available</span>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t14 || $[41] !== t17) {
    t18 = <div className="mt-1.5 flex items-center gap-3 text-sm">{t14}{t17}</div>;
    $[40] = t14;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t11 || $[44] !== t12 || $[45] !== t18) {
    t19 = <div className="flex-1">{t11}{t12}{t18}</div>;
    $[43] = t11;
    $[44] = t12;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Badge variant="outline" className="h-fit gap-1 rounded-full font-normal"><Lock className="size-3" /> Escrow</Badge>;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t10 || $[49] !== t19) {
    t21 = <Card><CardContent className="flex gap-3 pt-5">{t10}{t19}{t20}</CardContent></Card>;
    $[48] = t10;
    $[49] = t19;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== listing.sellerLabel) {
    t22 = listing.sellerLabel.replace("@", "").slice(0, 2);
    $[51] = listing.sellerLabel;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22) {
    t23 = <Avatar><AvatarFallback>{t22}</AvatarFallback></Avatar>;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== listing.sellerLabel) {
    t24 = <div className="text-sm font-semibold">{listing.sellerLabel}</div>;
    $[55] = listing.sellerLabel;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="text-xs text-muted-foreground">Seller · KYC verified</div>;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t24) {
    t26 = <div className="flex-1">{t24}{t25}</div>;
    $[58] = t24;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t23 || $[61] !== t26) {
    t27 = <div className="flex items-center gap-3">{t23}{t26}</div>;
    $[60] = t23;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== thread) {
    t28 = thread.map(_temp2);
    $[63] = thread;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t28) {
    t29 = <div className="space-y-3">{t28}</div>;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Separator />;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Label className="text-xs uppercase tracking-wide">Make a counter-offer</Label>;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = e => setOfferTokens(Math.max(1, Number(e.target.value) || 0));
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== listing.tokensRemaining || $[71] !== offerTokens) {
    t33 = <Input type="number" min={1} max={listing.tokensRemaining} value={offerTokens} onChange={t32} />;
    $[70] = listing.tokensRemaining;
    $[71] = offerTokens;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">Tokens</div>;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== t33) {
    t35 = <div>{t33}{t34}</div>;
    $[74] = t33;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = e_0 => setOfferPrice(Math.max(0, Number(e_0.target.value) || 0));
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== offerPrice) {
    t37 = <Input type="number" min={0} step="0.01" value={offerPrice} onChange={t36} />;
    $[77] = offerPrice;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">Price / token (USD)</div>;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== t37) {
    t39 = <div>{t37}{t38}</div>;
    $[80] = t37;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t35 || $[83] !== t39) {
    t40 = <div className="grid grid-cols-2 gap-2">{t35}{t39}</div>;
    $[82] = t35;
    $[83] = t39;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <span className="text-muted-foreground">Total</span>;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  const t42 = offerTokens * offerPrice;
  let t43;
  if ($[86] !== t42) {
    t43 = formatUsd(t42);
    $[86] = t42;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  let t44;
  if ($[88] !== t43) {
    t44 = <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-xs">{t41}<span className="font-semibold tabular-nums">{t43}</span></div>;
    $[88] = t43;
    $[89] = t44;
  } else {
    t44 = $[89];
  }
  let t45;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Sparkles className="size-4" />;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== sendOffer) {
    t46 = <Button variant="outline" className="w-full gap-1.5" onClick={sendOffer}>{t45} Send offer</Button>;
    $[91] = sendOffer;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== t40 || $[94] !== t44 || $[95] !== t46) {
    t47 = <div className="space-y-2">{t31}{t40}{t44}{t46}</div>;
    $[93] = t40;
    $[94] = t44;
    $[95] = t46;
    $[96] = t47;
  } else {
    t47 = $[96];
  }
  let t48;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Separator />;
    $[97] = t48;
  } else {
    t48 = $[97];
  }
  let t49;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = e_1 => setDraft(e_1.target.value);
    $[98] = t49;
  } else {
    t49 = $[98];
  }
  let t50;
  if ($[99] !== sendMessage) {
    t50 = e_2 => e_2.key === "Enter" && sendMessage();
    $[99] = sendMessage;
    $[100] = t50;
  } else {
    t50 = $[100];
  }
  let t51;
  if ($[101] !== draft || $[102] !== t50) {
    t51 = <Input placeholder={"Write a message\u2026"} value={draft} onChange={t49} onKeyDown={t50} />;
    $[101] = draft;
    $[102] = t50;
    $[103] = t51;
  } else {
    t51 = $[103];
  }
  let t52;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <Send className="size-4" />;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  let t53;
  if ($[105] !== sendMessage) {
    t53 = <Button onClick={sendMessage} size="icon" aria-label="Send">{t52}</Button>;
    $[105] = sendMessage;
    $[106] = t53;
  } else {
    t53 = $[106];
  }
  let t54;
  if ($[107] !== t51 || $[108] !== t53) {
    t54 = <div className="flex gap-2">{t51}{t53}</div>;
    $[107] = t51;
    $[108] = t53;
    $[109] = t54;
  } else {
    t54 = $[109];
  }
  let t55;
  if ($[110] !== t27 || $[111] !== t29 || $[112] !== t47 || $[113] !== t54) {
    t55 = <Card><CardContent className="space-y-4 pt-5">{t27}{t29}{t30}{t47}{t48}{t54}</CardContent></Card>;
    $[110] = t27;
    $[111] = t29;
    $[112] = t47;
    $[113] = t54;
    $[114] = t55;
  } else {
    t55 = $[114];
  }
  let t56;
  if ($[115] !== lastOffer) {
    t56 = lastOffer && <div className="sticky bottom-20 z-20 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur"><Button size="lg" className="w-full">Accept latest offer · {formatUsd(lastOffer.tokens * lastOffer.pricePerTokenUsd)}</Button><p className="mt-1.5 text-center text-[11px] text-muted-foreground">Buyer pays from platform balance · 1% platform fee · escrow releases on settlement</p></div>;
    $[115] = lastOffer;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  let t57;
  if ($[117] !== t21 || $[118] !== t55 || $[119] !== t56 || $[120] !== t8) {
    t57 = <div className="space-y-5">{t8}{t21}{t55}{t56}</div>;
    $[117] = t21;
    $[118] = t55;
    $[119] = t56;
    $[120] = t8;
    $[121] = t57;
  } else {
    t57 = $[121];
  }
  return t57;
}
function _temp2(m_0) {
  return <MessageBubble key={m_0.id} msg={m_0} />;
}
function _temp(m) {
  return m.offer;
}
function MessageBubble(t0) {
  const $ = _c(20);
  const {
    msg
  } = t0;
  const mine = msg.authorId === "self";
  const t1 = `flex w-full ${mine ? "justify-end" : "justify-start"}`;
  const t2 = `max-w-[80%] space-y-1 ${mine ? "items-end" : ""}`;
  const t3 = `text-[10px] uppercase tracking-wide ${mine ? "text-right" : ""} text-muted-foreground`;
  const t4 = msg.authorLabel;
  let t5;
  if ($[0] !== msg.at) {
    t5 = formatDateTime(msg.at);
    $[0] = msg.at;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== msg.authorLabel || $[3] !== t3 || $[4] !== t5) {
    t6 = <div className={t3}>{t4} · {t5}</div>;
    $[2] = msg.authorLabel;
    $[3] = t3;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = `rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-foreground text-background" : "bg-muted text-foreground"}`;
  let t8;
  if ($[6] !== mine || $[7] !== msg.offer) {
    t8 = msg.offer && <div className={`mt-2 rounded-lg border px-2.5 py-1.5 text-xs ${mine ? "border-background/40 text-background/90" : "border-foreground/15 text-muted-foreground"}`}>{formatNumber(msg.offer.tokens)} tokens ×{" "}{formatUsd(msg.offer.pricePerTokenUsd)} ={" "}{formatUsd(msg.offer.tokens * msg.offer.pricePerTokenUsd)}</div>;
    $[6] = mine;
    $[7] = msg.offer;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== msg.body || $[10] !== t7 || $[11] !== t8) {
    t9 = <div className={t7}>{msg.body}{t8}</div>;
    $[9] = msg.body;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t2 || $[14] !== t6 || $[15] !== t9) {
    t10 = <div className={t2}>{t6}{t9}</div>;
    $[13] = t2;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t1 || $[18] !== t10) {
    t11 = <div className={t1}>{t10}</div>;
    $[17] = t1;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}